import React, { useState, useEffect } from 'react';
import {
  FileText, DollarSign, TrendingUp, Clock, CheckCircle,
  BarChart3, PieChart, Users, Building
} from 'lucide-react';
import { STATUS_ORDER, STATUS_COLORS } from '../utils/constants';
import { formatNumber } from '../utils/helpers';
import { ProjectService } from '../services/firebaseService';

const Statistics = ({ userId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const result = await ProjectService.getUserStatistics(userId);
      if (result.success) {
        setStats(result.stats);
      }
      setLoading(false);
    };

    if (userId) {
      fetchStats();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center text-slate-500">
          <svg className="animate-spin h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Chargement des statistiques...
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center text-slate-500 py-8">
        Impossible de charger les statistiques
      </div>
    );
  }

  const tauxPaiement = stats.totalRevenue > 0
    ? ((stats.totalPaid / stats.totalRevenue) * 100).toFixed(1)
    : 0;

  const moyenneParProjet = stats.total > 0
    ? Math.round(stats.totalRevenue / stats.total)
    : 0;

  // Tri des statuts
  const sortedStatusCounts = STATUS_ORDER.map(status => ({
    status,
    count: stats.statusCounts[status] || 0,
    color: STATUS_COLORS[status]
  }));

  // Top 5 communes
  const topCommunes = Object.entries(stats.communeStats || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Statistiques agents
  const agentsList = Object.entries(stats.agentStats || {})
    .map(([name, data]) => ({
      name,
      ...data,
      tauxCompletion: data.count > 0 ? ((data.completed / data.count) * 100).toFixed(1) : 0
    }))
    .sort((a, b) => b.count - a.count);

  // Calcul max pour les barres
  const maxStatusCount = Math.max(...sortedStatusCounts.map(s => s.count), 1);
  const maxCommuneCount = topCommunes.length > 0 ? topCommunes[0][1] : 1;

  // Projets terminés
  const projetsTermines = stats.statusCounts['Fin Travaux'] || 0;
  const projetsActifs = stats.total - projetsTermines;

  // Projets favorables
  const projetsFavorables = (stats.statusCounts['Favorable'] || 0) +
    (stats.statusCounts['Autorisé'] || 0) +
    (stats.statusCounts['Chantier démarré'] || 0) +
    projetsTermines;

  return (
    <div className="space-y-6">
      {/* Cartes statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Affaires */}
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Affaires</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total}</p>
              <p className="text-xs text-slate-400 mt-1">Projets enregistrés</p>
            </div>
            <div className="p-3 bg-brand-100 rounded-xl">
              <FileText className="h-6 w-6 text-brand-700" />
            </div>
          </div>
        </div>

        {/* Revenus Total */}
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Revenus Total</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{formatNumber(stats.totalRevenue)}</p>
              <p className="text-xs text-slate-400 mt-1">Moy: {formatNumber(moyenneParProjet)} DH</p>
            </div>
            <div className="p-3 bg-green-100 rounded-xl">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Payé */}
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Montant Payé</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{formatNumber(stats.totalPaid)}</p>
              <p className="text-xs text-slate-400 mt-1">Taux: {tauxPaiement}%</p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-xl">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
        </div>

        {/* En Attente */}
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">En Attente</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{formatNumber(stats.totalPending)}</p>
              <p className="text-xs text-slate-400 mt-1">À percevoir</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-xl">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par statut */}
        <div className="card p-5">
          <div className="flex items-center mb-4">
            <BarChart3 className="h-5 w-5 text-slate-500 mr-2" />
            <h3 className="font-semibold text-slate-900">Répartition par Statut</h3>
          </div>
          <div className="space-y-3">
            {sortedStatusCounts.map(({ status, count, color }) => (
              <div key={status} className="flex items-center">
                <span className="w-36 text-sm text-slate-600 truncate">{status}</span>
                <div className="flex-1 mx-3">
                  <div className="h-6 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(count / maxStatusCount) * 100}%`,
                        backgroundColor: color
                      }}
                    />
                  </div>
                </div>
                <span className="w-16 text-sm font-medium text-slate-900 text-right">
                  {count} ({stats.total > 0 ? ((count / stats.total) * 100).toFixed(0) : 0}%)
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Communes */}
        <div className="card p-5">
          <div className="flex items-center mb-4">
            <Building className="h-5 w-5 text-slate-500 mr-2" />
            <h3 className="font-semibold text-slate-900">Top 5 Communes</h3>
          </div>
          {topCommunes.length > 0 ? (
            <div className="space-y-3">
              {topCommunes.map(([commune, count], index) => (
                <div key={commune} className="flex items-center">
                  <span className="w-6 text-sm font-medium text-slate-400">{index + 1}.</span>
                  <span className="w-32 text-sm text-slate-600 truncate">{commune}</span>
                  <div className="flex-1 mx-3">
                    <div className="h-5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                        style={{ width: `${(count / maxCommuneCount) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className="w-12 text-sm font-medium text-slate-900 text-right">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm italic">Aucune donnée disponible</p>
          )}
        </div>
      </div>

      {/* Indicateurs de performance */}
      <div className="card p-5">
        <div className="flex items-center mb-4">
          <PieChart className="h-5 w-5 text-slate-500 mr-2" />
          <h3 className="font-semibold text-slate-900">Indicateurs de Performance</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-xl">
            <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-700">{projetsTermines}</p>
            <p className="text-xs text-green-600">Terminés</p>
            <p className="text-xs text-slate-500 mt-1">
              {stats.total > 0 ? ((projetsTermines / stats.total) * 100).toFixed(1) : 0}% du total
            </p>
          </div>
          <div className="text-center p-4 bg-brand-50 rounded-xl">
            <Clock className="h-8 w-8 text-brand-700 mx-auto mb-2" />
            <p className="text-2xl font-bold text-brand-700">{projetsActifs}</p>
            <p className="text-xs text-brand-700">Actifs</p>
            <p className="text-xs text-slate-500 mt-1">En traitement</p>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-xl">
            <TrendingUp className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-700">{projetsFavorables}</p>
            <p className="text-xs text-emerald-600">Favorables</p>
            <p className="text-xs text-slate-500 mt-1">Avis positif</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-xl">
            <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-700">{agentsList.length}</p>
            <p className="text-xs text-purple-600">Agents</p>
            <p className="text-xs text-slate-500 mt-1">Responsables</p>
          </div>
        </div>
      </div>

      {/* Tableau Performance par agent */}
      {agentsList.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center mb-4">
            <Users className="h-5 w-5 text-slate-500 mr-2" />
            <h3 className="font-semibold text-slate-900">Performance par Agent</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Agent</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Projets</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Revenus</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Terminés</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Taux</th>
                </tr>
              </thead>
              <tbody>
                {agentsList.map((agent, index) => (
                  <tr key={agent.name} className={index % 2 === 0 ? 'bg-slate-50' : ''}>
                    <td className="py-3 px-4 text-sm font-medium text-slate-900">{agent.name}</td>
                    <td className="py-3 px-4 text-sm text-slate-600 text-right">{agent.count}</td>
                    <td className="py-3 px-4 text-sm text-slate-600 text-right">{formatNumber(agent.revenue)} DH</td>
                    <td className="py-3 px-4 text-sm text-slate-600 text-right">{agent.completed}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${
                        parseFloat(agent.tauxCompletion) >= 75 ? 'text-green-600' :
                        parseFloat(agent.tauxCompletion) >= 50 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {agent.tauxCompletion}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistics;
